// Click-to-copy affordance for cell renderers. Plain DOM with a direct listener rather than a
// Stimulus controller: AG-Grid destroys and rebuilds cell elements on every scroll, so the
// renderer that creates the button is what owns its lifecycle.

export const COPY_BUTTON_CLASS = "ag-grid-copy-button";

const COPY_TITLE = "Copy";
const COPIED_TITLE = "Copied!";
// Long enough to read the tick, short enough to keep up with copying down a column.
const FEEDBACK_MS = 1200;

export function buildCopyButton(text: string): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = COPY_BUTTON_CLASS;
  button.title = COPY_TITLE;
  button.setAttribute("aria-label", `${COPY_TITLE} ${text}`);
  button.draggable = false; // avoid hijacking AG Grid's cell range drag-select

  const icon = document.createElement("i");
  icon.className = "fas fa-copy";
  icon.setAttribute("aria-hidden", "true");
  button.appendChild(icon);

  let resetTimer: number | undefined;

  const showCopied = () => {
    icon.className = "fas fa-check";
    button.title = COPIED_TITLE;
    button.classList.add(`${COPY_BUTTON_CLASS}--copied`);

    window.clearTimeout(resetTimer);
    resetTimer = window.setTimeout(() => {
      icon.className = "fas fa-copy";
      button.title = COPY_TITLE;
      button.classList.remove(`${COPY_BUTTON_CLASS}--copied`);
    }, FEEDBACK_MS);
  };

  // The button sits inside cells whose own click impersonates the user — both events stop
  // here, since AG-Grid starts a range selection on mousedown that would swallow the click.
  button.addEventListener("mousedown", (event) => event.stopPropagation());
  button.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    void copyText(text).then((copied) => {
      if (copied) showCopied();
    });
  });

  return button;
}

// navigator.clipboard is secure-context only, so a non-HTTPS host falls back to the textarea
// path — same two-step the copy-to-os-clipboard Stimulus controller uses.
async function copyText(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Denied, or a non-secure context — fall through rather than failing silently.
    }
  }
  return legacyCopy(text);
}

function legacyCopy(text: string): boolean {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  // Off-screen but still focusable: `display: none` would make select() a no-op.
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.select();

  const copied = document.execCommand("copy");
  textArea.remove();
  return copied;
}
